import './NavBar.css';

import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import HebrewHeartsLogo from '../../assets/images/hebrew-hearts-logo.png';

import { Badge } from '../ui/badge';

import { BsFillLightningChargeFill } from 'react-icons/bs';
import {
  FaCalendarCheck,
  FaHeart,
  FaNewspaper,
  FaSearch,
  FaUsers,
  FaHome,
  FaSignOutAlt,
  FaVideo,
  FaGift,
  FaTrophy,
} from 'react-icons/fa';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { isSpeedDateEnabled } from '../../config/featureFlags';

import AccountCircle
  from '@mui/icons-material/AccountCircle'; // For user profile icon
import MailIcon from '@mui/icons-material/Mail'; // For messages icon
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon
  from '@mui/icons-material/Notifications'; // For notifications icon
import AppBar from '@mui/material/AppBar';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Popper from '@mui/material/Popper';
import { useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import useMediaQuery from '@mui/material/useMediaQuery';

import { logoutUser, loginUser } from '../../Store/actions/userActions';
import { useAppTheme } from '../../contexts/ThemeContext';
import NavLinks from './components/NavLinks';
// Assuming NotificationDropdown and ProfileDropdown are adapted to Material UI components
import NotificationDropdown from './components/NotificationDropdown';
import ProfileDropdown from './components/ProfileDropdown';
import { ThemeToggle } from '../ui/ThemeToggle';

const NavBar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Custom theme hook
  const { isDarkMode } = useAppTheme();
  
  // Redux Persist will handle the state persistence automatically
  // No need to manually check localStorage here

  const userInfo = useSelector((state) => state.user);
  const isLoggedIn = !!(userInfo && userInfo.isAuthenticated && userInfo.user);
  const isCompactNav = useMediaQuery(
    muiTheme.breakpoints.down(isLoggedIn ? "lg" : "md")
  );
  // Popper state
  const [openNotificationPopper, setOpenNotificationPopper] = useState(false);
  const [openProfilePopper, setOpenProfilePopper] = useState(false);

  // Refs for anchor elements
  const notificationRef = useRef(null);
  const profileRef = useRef(null);

  // Toggle functions for poppers
  const handleToggleNotifications = () => {
    setOpenNotificationPopper((prevOpen) => !prevOpen);
  };

  const handleToggleProfile = () => {
    setOpenProfilePopper((prevOpen) => !prevOpen);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (route, event) => {
    event.preventDefault(); // Prevent default action (for anchor tags)
    event.stopPropagation(); // Stop event from bubbling up
    console.log("Navigating to:", route);
    if (NotificationDropdown || ProfileDropdown) {
      setOpenNotificationPopper(false);
      setOpenProfilePopper(false);
    }
    navigate(route);
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  // Helper function to check if a tab is active
  const isActiveTab = (path) => {
    if (path === "/" && location.pathname === "/") {
      return true;
    }
    if (path !== "/" && location.pathname.startsWith(path)) {
      return true;
    }
    return false;
  };

  const handleLogoutClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    console.log("Logging Out - Starting logout process");
    console.log("Current user state before logout:", userInfo);
    
    try {
      // Close mobile drawer first
      if (mobileOpen) {
        setMobileOpen(false);
      }
      
      // Close any open dropdowns
      setOpenNotificationPopper(false);
      setOpenProfilePopper(false);
      
      // Clear all localStorage manually first
      localStorage.clear();
      sessionStorage.clear();
      
      // Dispatch logout action to update Redux state
      await dispatch(logoutUser());
      
      console.log("Logout action completed, navigating to home");
      
      // Navigate to home page
      navigate("/");
      
      console.log("Logging Out - Logout process completed");
      
      // Force a page reload to ensure state is cleared
      setTimeout(() => {
        window.location.reload();
      }, 500);
      
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const loggedInDrawerItems = [
    { icon: <FaHome />, label: "Home", path: "/dashboard" },
    { icon: <FaHeart />, label: "Matches", path: "/matches" },
    { icon: <MailIcon />, label: "Messages", path: "/messages" },
    { icon: <FaGift />, label: "Referrals", path: "/referrals" },
    { icon: <FaTrophy />, label: "Leaderboard", path: "/leaderboard" },
    ...(isSpeedDateEnabled()
      ? [{ icon: <FaVideo />, label: "Speed Date", path: "/speed-date" }]
      : []),
    { icon: <AccountCircle />, label: "Account", path: "/account" },
  ];

  const loggedOutDrawerItems = [
    { icon: <FaHome />, label: "Home", path: "/" },
    { icon: <FaSearch />, label: "How it works", path: "/how-it-works" },
    { icon: <FaCalendarCheck />, label: "Pricing", path: "/pricing" },
    { icon: <AccountCircle />, label: "Login", path: "/login" },
    { icon: <FaUsers />, label: "Join waitlist", path: "/#join" },
  ];

  const renderDrawerItem = (item, extraClass = "") => (
    <ListItem
      button
      key={item.path}
      onClick={(event) => handleNavigation(item.path, event)}
      className={`navbar-v2-menu-item${isActiveTab(item.path) ? " navbar-v2-menu-item--active" : ""}${extraClass ? ` ${extraClass}` : ""}`}
    >
      <ListItemIcon className="navbar-v2-menu-icon">{item.icon}</ListItemIcon>
      <ListItemText primary={item.label} />
    </ListItem>
  );

  const drawerContent = (
    <div className="navbar-v2-menu navbar-v2-menu--drawer">
      <List className="navbar-v2-menu-list" disablePadding>
        {(isLoggedIn ? loggedInDrawerItems : loggedOutDrawerItems).map((item) =>
          renderDrawerItem(item)
        )}
      </List>
      {isLoggedIn && (
        <>
          <div className="navbar-v2-menu-divider" />
          <List className="navbar-v2-menu-list" disablePadding>
            <ListItem
              button
              onClick={handleLogoutClick}
              className="navbar-v2-menu-item navbar-v2-menu-item--logout"
            >
              <ListItemIcon className="navbar-v2-menu-icon">
                <FaSignOutAlt />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          </List>
        </>
      )}
    </div>
  );

  useEffect(() => {
    // Logic for handling click outside for notifications and profile dropdowns goes here
    const handleClickOutside = (event) => {
      // Implement similar logic to the first file for handling outside clicks
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <AppBar
        position="static"
        style={{ 
          backgroundColor: "var(--navbar-bg)", 
          color: "var(--navbar-text)",
          borderBottom: "1px solid var(--navbar-border)",
          boxShadow: "none",
          zIndex: 20
        }}
      >
        <Toolbar
          className={`navbar-v2-toolbar${isCompactNav ? " navbar-v2-toolbar--compact" : ""}`}
        >
          {isCompactNav && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1, flexShrink: 0 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <div
            className="navbar-v2-brand"
            onClick={(event) => handleNavigation(isLoggedIn ? "/dashboard" : "/", event)}
          >
            <img
              src={HebrewHeartsLogo}
              alt="Hebrew Hearts Logo"
              className={`navbar-v2-logo ${isCompactNav ? "navbar-v2-logo--compact" : "navbar-v2-logo--desktop"}`}
            />
            <h2 className="navbar-v2-title">Hebrew Hearts</h2>
            <Badge className="navbar-v2-beta ml-2 bg-violet-600/90 text-white text-xs">Beta</Badge>
          </div>

          {isCompactNav && <div className="navbar-v2-spacer" />}

          {!isCompactNav && !isLoggedIn && (
            <nav className="navbar-v2-links" aria-label="Main navigation">
              <Link
                to="/how-it-works"
                onClick={(event) => handleNavigation("/how-it-works", event)}
                className={`navbar-v2-link${isActiveTab("/how-it-works") ? " navbar-v2-link--active" : ""}`}
              >
                How it works
              </Link>
              <Link
                to="/pricing"
                onClick={(event) => handleNavigation("/pricing", event)}
                className={`navbar-v2-link${isActiveTab("/pricing") ? " navbar-v2-link--active" : ""}`}
              >
                Pricing
              </Link>
            </nav>
          )}
          {!isCompactNav && isLoggedIn && (
            <nav className="navbar-v2-links" aria-label="Main navigation">
              <Link
                to="/dashboard"
                onClick={(event) => handleNavigation("/dashboard", event)}
                className={`navbar-v2-link${isActiveTab("/dashboard") ? " navbar-v2-link--active" : ""}`}
              >
                Home
              </Link>
              <Link
                to="/matches"
                onClick={(event) => handleNavigation("/matches", event)}
                className={`navbar-v2-link${isActiveTab("/matches") ? " navbar-v2-link--active" : ""}`}
              >
                Matches
              </Link>
              <Link
                to="/messages"
                onClick={(event) => handleNavigation("/messages", event)}
                className={`navbar-v2-link${isActiveTab("/messages") ? " navbar-v2-link--active" : ""}`}
              >
                Messages
              </Link>
              <Link
                to="/referrals"
                onClick={(event) => handleNavigation("/referrals", event)}
                className={`navbar-v2-link${isActiveTab("/referrals") ? " navbar-v2-link--active" : ""}`}
              >
                Referrals
              </Link>
              <Link
                to="/leaderboard"
                onClick={(event) => handleNavigation("/leaderboard", event)}
                className={`navbar-v2-link${isActiveTab("/leaderboard") ? " navbar-v2-link--active" : ""}`}
              >
                Leaderboard
              </Link>
              <Link
                to="/speed-date"
                onClick={(event) => handleNavigation("/speed-date", event)}
                className={`navbar-v2-link${isActiveTab("/speed-date") ? " navbar-v2-link--active" : ""}`}
              >
                Speed Date
              </Link>
              <Link
                to="/account"
                onClick={(event) => handleNavigation("/account", event)}
                className={`navbar-v2-link${isActiveTab("/account") ? " navbar-v2-link--active" : ""}`}
              >
                Account
              </Link>
            </nav>
          )}

          <div className="navbar-v2-actions">
            <ThemeToggle
              variant="navbar"
              size="medium"
              showSystemOption={false}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            />

            {!isLoggedIn ? (
              !isCompactNav && (
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={(event) => handleNavigation("/#join", event)}
                    style={{
                      display: "flex",
                      minWidth: "84px",
                      maxWidth: "480px",
                      cursor: "pointer",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      borderRadius: "9999px",
                      height: "40px",
                      padding: "0 16px",
                      backgroundColor: "var(--navbar-text)",
                      color: "var(--navbar-bg)",
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: "14px",
                      fontWeight: "700",
                      lineHeight: "normal",
                      letterSpacing: "0.01em",
                      border: "none"
                    }}
                  >
                    <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      Join Waitlist
                    </span>
                  </button>
                  <button
                    onClick={(event) => handleNavigation("/login", event)}
                    style={{
                      display: "flex",
                      minWidth: "84px",
                      maxWidth: "480px",
                      cursor: "pointer",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      borderRadius: "9999px",
                      height: "40px",
                      padding: "0 16px",
                      backgroundColor: "transparent",
                      color: "var(--navbar-text)",
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: "14px",
                      fontWeight: "600",
                      lineHeight: "normal",
                      letterSpacing: "0.01em",
                      border: "1.5px solid var(--navbar-border)"
                    }}
                  >
                    <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      Log In
                    </span>
                  </button>
                </div>
              )
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                {!isCompactNav && (
                  <div style={{ paddingRight: "8px" }}>
                    {/* Notification Bell - Only show when logged in and not in mobile view */}
                    <button
                      ref={notificationRef}
                      onClick={handleToggleNotifications}
                      style={{
                        display: "flex",
                        maxWidth: "480px",
                        cursor: "pointer",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        borderRadius: "9999px",
                        height: "40px",
                        backgroundColor: "#ededed",
                        color: "#141414",
                        gap: "8px",
                        fontSize: "14px",
                        fontWeight: "bold",
                        lineHeight: "normal",
                        letterSpacing: "0.015em",
                        minWidth: "0",
                        padding: "0 10px",
                        border: "none"
                      }}
                    >
                      <div style={{ color: "#141414" }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" fill="currentColor" viewBox="0 0 256 256">
                          <path
                            d="M221.8,175.94C216.25,166.38,208,139.33,208,104a80,80,0,1,0-160,0c0,35.34-8.26,62.38-13.81,71.94A16,16,0,0,0,48,200H88.81a40,40,0,0,0,78.38,0H208a16,16,0,0,0,13.8-24.06ZM128,216a24,24,0,0,1-22.62-16h45.24A24,24,0,0,1,128,216ZM48,184c7.7-13.24,16-43.92,16-80a64,64,0,1,1,128,0c0,36.05,8.28,66.73,16,80Z"
                          ></path>
                        </svg>
                      </div>
                    </button>
                    {openNotificationPopper && (
                      <ClickAwayListener
                        onClickAway={() => setOpenNotificationPopper(false)}
                      >
                        <Popper
                          open={openNotificationPopper}
                          anchorEl={notificationRef.current}
                          role={undefined}
                          placement="bottom-start"
                          style={{ zIndex: 9999 }}
                        >
                          <NotificationDropdown />
                        </Popper>
                      </ClickAwayListener>
                    )}
                  </div>
                )}
                <div>
                  {/* Profile Avatar */}
                  <div
                    ref={profileRef}
                    onClick={handleToggleProfile}
                    style={{
                      backgroundImage: `url("${userInfo?.user?.profileImages?.[0] || userInfo?.user?.avatar || userInfo?.user?.profilePicture || 'https://lh3.googleusercontent.com/aida-public/AB6AXuCI0S0UZ9I-mkgx5L-BJEFDs07_ue_xafqB3OCXCWDEpUXisJGGqOnLuOaAMkVZ7burB8dCWTF9LUnkATkPv1meNJG7ffjPsGFzCk4FjTpHFceuphDAZulhbdb_gJDfS3x-j2T-inbMsa9eOAYRqy81oUiRjYmqemnLxiHu2I8q2J27c0R53R-ixKYFdmyorrkRTnK2pe32qhGqbuJ2wg0_75xT3IvadvLhTKRWUCKWCd5jNzte7oi1MxHgm-Ilqzq4sp00fRBbs6Q'}")`,
                      backgroundCenter: "center",
                      backgroundRepeat: "no-repeat",
                      aspectRatio: "1",
                      backgroundSize: "cover",
                      borderRadius: "50%",
                      width: "40px",
                      height: "40px",
                      cursor: "pointer"
                    }}
                  />
                  {openProfilePopper && (
                    <ClickAwayListener
                      onClickAway={() => setOpenProfilePopper(false)}
                    >
                      <Popper
                        open={openProfilePopper}
                        anchorEl={profileRef.current}
                        role={undefined}
                        placement="bottom-start"
                        style={{ zIndex: 9999 }}
                      >
                        <ProfileDropdown
                          handleNavigation={handleNavigation}
                          handleLogout={handleLogoutClick}
                        />
                      </Popper>
                    </ClickAwayListener>
                  )}
                </div>
              </div>
            )}
          </div>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          display: isCompactNav ? "block" : "none",
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 280,
            backgroundColor: "var(--navbar-bg)",
            color: "var(--navbar-text)",
            borderRight: "1px solid var(--navbar-border)",
          },
        }}
      >
        {/* Render NavLinks inside the drawer for xs screens */}

        {drawerContent}
      </Drawer>
      {/* Conditional rendering for NotificationDropdown and ProfileDropdown components */}
    </>
  );
};

export default NavBar;
